const ATTRIBUTION_KEY = 'emblem-attribution';

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'msclkid'
];

const safeNowIso = () => {
  try {
    return new Date().toISOString();
  } catch (error) {
    return '';
  }
};

const parseCampaignParams = (search = '') => {
  const params = new URLSearchParams(search || '');
  const parsed = {};

  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      parsed[key] = value;
    }
  });

  return parsed;
};

const hasCampaignParams = (campaign) => {
  return Object.keys(campaign).length > 0;
};

const readStorage = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed;
    }
    return {};
  } catch (error) {
    return {};
  }
};

const writeStorage = (value) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(value));
  } catch (error) {
    // Ignore storage failures
  }
};

const normalizeSearch = (search = '') => {
  if (!search) {
    return '';
  }
  return search.startsWith('?') ? search : `?${search}`;
};

export const captureAttributionFromUrl = ({ pathname = '/', search = '' } = {}) => {
  if (typeof window === 'undefined') {
    return;
  }

  const campaign = parseCampaignParams(search);
  const referrer = document.referrer || '';
  const now = safeNowIso();
  const existing = readStorage();

  const touch = {
    capturedAt: now,
    path: pathname,
    search: normalizeSearch(search),
    referrer,
    ...campaign
  };

  let next = { ...existing };

  if (hasCampaignParams(campaign)) {
    if (!existing.firstTouch || !existing.firstTouch.capturedAt) {
      next.firstTouch = touch;
    }
    next.lastTouch = touch;
  } else if (!existing.firstTouch && !existing.lastTouch) {
    next.firstTouch = {
      capturedAt: now,
      path: pathname,
      search: normalizeSearch(search),
      referrer,
      utm_source: referrer ? 'referral' : 'direct',
      utm_medium: referrer ? 'referral' : 'none'
    };
    next.lastTouch = next.firstTouch;
  }

  if (!next.firstTouch && existing.firstTouch) {
    next.firstTouch = existing.firstTouch;
  }
  if (!next.lastTouch && existing.lastTouch) {
    next.lastTouch = existing.lastTouch;
  }

  writeStorage(next);
};

const flattenTouch = (prefix, touch = {}) => {
  const normalized = touch || {};

  return {
    [`${prefix}CapturedAt`]: normalized.capturedAt || '',
    [`${prefix}Path`]: normalized.path || '',
    [`${prefix}Search`]: normalized.search || '',
    [`${prefix}Referrer`]: normalized.referrer || '',
    [`${prefix}UtmSource`]: normalized.utm_source || '',
    [`${prefix}UtmMedium`]: normalized.utm_medium || '',
    [`${prefix}UtmCampaign`]: normalized.utm_campaign || '',
    [`${prefix}UtmContent`]: normalized.utm_content || '',
    [`${prefix}UtmTerm`]: normalized.utm_term || '',
    [`${prefix}Gclid`]: normalized.gclid || '',
    [`${prefix}Fbclid`]: normalized.fbclid || '',
    [`${prefix}Msclkid`]: normalized.msclkid || ''
  };
};

export const getLeadAttributionPayload = () => {
  if (typeof window === 'undefined') {
    return {};
  }

  const existing = readStorage();
  const firstTouch = existing.firstTouch || {};
  const lastTouch = existing.lastTouch || {};

  return {
    ...flattenTouch('firstTouch', firstTouch),
    ...flattenTouch('lastTouch', lastTouch),
    currentPath: window.location.pathname || '',
    currentSearch: window.location.search || ''
  };
};
